import { useEffect, useState } from 'react';
import { Drawer } from './ui/Drawer';
import api from '../services/api';

interface ChainLeg {
  pronumber: number;
  legnum: number;
  legstatus: string;
  origin: { city: string | null; state: string | null };
  destination: { city: string | null; state: string | null };
  appt_at: string | null;
  is_otr: boolean;
}

interface ChainResponse {
  driver: string;
  legs: ChainLeg[];
}

interface Props {
  driver: string | null;
  onClose: () => void;
}

function formatCityState(loc: { city: string | null; state: string | null }): string {
  if (!loc.city && !loc.state) return '—';
  if (!loc.state) return loc.city ?? '—';
  if (!loc.city) return loc.state;
  return `${loc.city}, ${loc.state}`;
}

function formatApptAt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function OTRByLocationChainDrawer({ driver, onClose }: Props) {
  const [chain, setChain] = useState<ChainResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!driver) {
      setChain(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<ChainResponse>(`/otr-by-location/${encodeURIComponent(driver)}/chain`)
      .then((res) => {
        if (cancelled) return;
        setChain(res.data);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(e?.message ?? 'Failed to load chain');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [driver]);

  return (
    <Drawer
      isOpen={driver !== null}
      onClose={onClose}
      title={driver ? `Queued legs — ${driver}` : 'Queued legs'}
      maxWidth="lg"
    >
      {loading && <div className="p-2 text-sm text-gray-600 dark:text-gray-400">Loading…</div>}
      {error && <div className="p-2 text-sm text-red-700">{error}</div>}
      {!loading && !error && chain && chain.legs.length === 0 && (
        <div className="p-2 text-sm text-gray-600 dark:text-gray-400">No queued legs.</div>
      )}
      {!loading && !error && chain && chain.legs.length > 0 && (
        <ol className="space-y-3 p-2">
          {chain.legs.map((leg, idx) => (
            <li
              key={`${leg.pronumber}-${leg.legnum}`}
              className={`rounded border p-2 ${leg.is_otr ? 'border-orange-300 bg-orange-50 dark:bg-orange-950/30' : 'border-gray-200 bg-panel dark:border-gray-700'}`}
            >
              <div className="flex items-center justify-between text-xs text-muted">
                <span>
                  #{idx + 1} · PRO {leg.pronumber}-{leg.legnum}
                </span>
                <span>{leg.legstatus}</span>
              </div>
              <div className="mt-1 text-sm">
                <span>{formatCityState(leg.origin)}</span>
                <span className="mx-1 text-gray-400 dark:text-gray-500">→</span>
                <span>{formatCityState(leg.destination)}</span>
                {leg.is_otr && (
                  <span className="ml-2 inline-block rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800">
                    OTR
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">Appt: {formatApptAt(leg.appt_at)}</div>
            </li>
          ))}
        </ol>
      )}
    </Drawer>
  );
}
